import Mpegts from 'mpegts.js';
import CustomWebSocketLoader from '../../custom-websocket-loader';
import { callbacks } from '../../callbacks';
import {DiscordSDK, Events, Types} from '@discord/embedded-app-sdk';
import { clientId, getSurfWsUrl } from '../../consts';
import { getAuthCode } from '../../DiscordUtils';
import { getSession } from '../../api';
import { DiscordUser, LoadStatus, SessionDetails } from '../../Types';
import UserPfp from '../UserPfp';
import LoadingSpinner from '../../LoadingSpinner';
import React, { useEffect, useState } from 'react';
import './VmView.css';
import { toast, ToastContainer } from 'react-toastify';

export default function VmView() {
  const containerRef = React.useRef<HTMLVideoElement>(null);
  const [wsState,setWsState] = useState<WebSocket | null>(null);
  const [discordSdk, setDiscordSdk] = useState<DiscordSDK|undefined>(undefined);
  const [users,setUsers] = useState<DiscordUser[]>([]);
  const [whoHasCursor,setWhoHasCursor] = useState<string|undefined>(undefined);
  const [currentUserId,setCurrentUserId] = useState<string|undefined>(undefined);
  const lastMousePos = {x:0,y:0};
  const [loadStatus, setLoadStatus] = useState<LoadStatus>(LoadStatus.IDLE);
  const [showDebug, setShowDebug] = useState<boolean>(false);
  const [playerStats, setPlayerStats] = useState<any>(undefined);
  const [authed, setAuthed] = useState<boolean>(false);

  function currentUserHasCursor(): boolean {
    return whoHasCursor !== undefined && whoHasCursor === currentUserId;
  }

  async function onCustomMsg(data: any) {
    if (discordSdk === undefined) return;
    if (data[0] === "{"){
      const msg = JSON.parse(data);
      if (msg.type && msg.type === "roomState"){
        setWhoHasCursor(msg.userWithCursor);
      }
    }
  }
  callbacks.onCustomMsg = onCustomMsg;
  callbacks.onWs = (newWs)=>{
    if (wsState && wsState !== newWs){
      if (wsState.readyState === wsState.OPEN) wsState.close();
      wsState.onerror = null;
      wsState.onclose = null;
    }
    setWsState(newWs);
    newWs.onerror = async (e) => {
      console.error('WebSocket error:', e);
      setLoadStatus(LoadStatus.ERROR);
    }
    newWs.onclose = async (e) => {
      console.error('WebSocket error:', e);
      setLoadStatus(LoadStatus.ERROR);
    }
  };

  function start(roomUrl:string){
    if (containerRef.current === null) {
      console.error("Container reference is null");
      return;
    }

    var mse = Mpegts.createPlayer({
      type: "mpegts",
      url: roomUrl,
      isLive: true,
    },{
      isLive:true,
      enableWorker:true,
      enableWorkerForMSE: true,
      /*liveBufferLatencyMaxLatency:.6,
      liveBufferLatencyMinRemain:0,
      liveBufferLatencyChasing:true,
      liveBufferLatencyChasingOnPaused:true,*/
      liveSync:true,
      liveSyncMaxLatency:0.3,
      liveSyncTargetLatency:0.1,
      liveSyncPlaybackRate:1.2,
      enableStashBuffer:false,// @ts-ignore
      customLoader: CustomWebSocketLoader,
    }) as Mpegts.MSEPlayer;
    mse.attachMediaElement(containerRef.current);

    mse.on(Mpegts.Events.ERROR, (e) => {
      //console.error('MSE Error:', e);
      setLoadStatus(LoadStatus.ERROR);
    });
    mse.on(Mpegts.Events.MEDIA_INFO, (e) => {
      //console.log('MSE Media Info:', e);
      setLoadStatus(LoadStatus.STREAMING);
    });
    mse.on(Mpegts.Events.STATISTICS_INFO, (e) => {
      setPlayerStats(e);
    });

    mse.load();
    mse.play();
  }

  function onMouseMove(e:React.MouseEvent<HTMLVideoElement, MouseEvent>){
    const rpos = mapVidPos(e.pageX,e.pageY);
    const xPct = Math.min(Math.max(rpos.x,0),1);
    const yPct = Math.min(Math.max(rpos.y,0),1);
    if (xPct === lastMousePos.x && yPct === lastMousePos.y) return;
    // check if position is closer than 2% to last position
    if (Math.abs(xPct - lastMousePos.x) < 0.03 && Math.abs(yPct - lastMousePos.y) < 0.01) return;
    lastMousePos.x = xPct;
    lastMousePos.y = yPct;
    if (wsState && wsState.readyState === wsState.OPEN){
      wsState.send(JSON.stringify({action:"mouseMove",x:xPct,y:yPct}));
    }
  }

  function onMouseUpDown(e:React.MouseEvent<HTMLVideoElement, MouseEvent>){
    if (wsState && wsState.readyState === wsState.OPEN){
      const buttonName = e.button === 0 ? "left" : e.button === 1 ? "middle" : "right";
      wsState.send(JSON.stringify({action:"mouseClick",down:e.type === "mousedown",button:buttonName}));
      //e.preventDefault();
    }
  }

  function onScroll(e:React.WheelEvent<HTMLVideoElement>){
    console.log(e);
    if (wsState && wsState.readyState === wsState.OPEN){
      wsState.send(JSON.stringify({action:"scroll",x:0,y:e.deltaY>0?1:-1}));
      e.preventDefault();
    }
  }

  // Key event handling
  useEffect(() => {
    function onKeyUpDown(this:Document, e:KeyboardEvent){
      if (wsState && wsState.readyState === wsState.OPEN){
        wsState.send(JSON.stringify({action:"key",down:e.type === "keydown",key:e.key}));
      }
    }
    document.addEventListener("keydown",onKeyUpDown);
    document.addEventListener("keyup",onKeyUpDown);
    return () => {
      document.removeEventListener("keydown",onKeyUpDown);
      document.removeEventListener("keyup",onKeyUpDown);
    }
  },[wsState]);

  function passCursorToUser(userId:string){
    if (wsState && wsState.readyState === wsState.OPEN){
      wsState.send(JSON.stringify({action:"passCursor",userId:userId}));
    }
  }

  function participantsUpdate(participants: Types.GetActivityInstanceConnectedParticipantsResponse){
    setUsers(participants.participants);
  }

  function getVideoPos(){
    if (!containerRef.current) return {x:0,y:0,width:0,height:0};
    if (containerRef.current.videoWidth === 0 || containerRef.current.videoHeight === 0) return {x:0,y:0,width:0,height:0};
    const rect = containerRef.current.getBoundingClientRect();
    const scale = Math.min(rect.width / containerRef.current.videoWidth, rect.height / containerRef.current.videoHeight);
    const vidWidth = containerRef.current.videoWidth * scale;
    const vidHeight = containerRef.current.videoHeight * scale;
    const x = (rect.width - vidWidth) / 2 + rect.left;
    const y = (rect.height - vidHeight) / 2 + rect.top;
    return {x:x,y:y,width:vidWidth,height:vidHeight};
  }

  function mapVidPos(x:number,y:number){
    const vpos = getVideoPos();
    if (vpos.width === 0 || vpos.height === 0) return {x:0,y:0};
    const relX = (x - vpos.x) / vpos.width;
    const relY = (y - vpos.y) / vpos.height;
    return {x:relX,y:relY};
  }

  async function initRoom(sdk:DiscordSDK){
    const authCode = await getAuthCode(sdk);
    if (!authCode) {
      console.error('Failed to get authorization code');
      return;
    }
    setLoadStatus(LoadStatus.REQUESTING_ROOM);
    let res: SessionDetails|undefined = undefined;
    try{
      res = await getSession(sdk.instanceId, authCode)
    }catch(err:any){
      setLoadStatus(LoadStatus.ERROR_SPROOM);
      return;
    }
    if (!res) {
      console.error('Failed to get session details');
      setLoadStatus(LoadStatus.ERROR);
      return;
    }
    if (!authed){
      await sdk.commands.authenticate({access_token:res.at});
      sdk.subscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, participantsUpdate);
      setAuthed(true);
    }
    setUsers((await sdk.commands.getInstanceConnectedParticipants()).participants);
    const code = res.code;
    const roomid = res.roomid;
    setCurrentUserId(res.userId);
    if (!code || !roomid) {
      console.error('Invalid session data:', res);
      return;
    }
    const roomUrl = getSurfWsUrl(roomid,code);

    setLoadStatus(LoadStatus.WAITING_FOR_MEDIA);
    start(roomUrl);
  }

  // Discord SDK handling
  useEffect(() => {
    async function setupSdk(){
      if (discordSdk){
        return;
      }
      try{
        const sdk = new DiscordSDK(clientId);
        await sdk.ready();
        console.log('Discord SDK initialized');
        setDiscordSdk(sdk);
        await initRoom(sdk);
      }catch (err) {
        console.error('Failed to initialize Discord SDK:', err);
        return;
      }
    }
    async function closeSdk(){
      if (discordSdk)
      discordSdk.unsubscribe(Events.ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE, participantsUpdate);
    }

    setupSdk();
    return () => {
      closeSdk();
    }
  },[]);

  useEffect(() => {
    if (currentUserId && whoHasCursor === currentUserId){
      toast.info("You now have the cursor",{position: "bottom-right",autoClose: 3000,theme:"dark"});
    }
  },[whoHasCursor]);

  const whoHasCursorUser = users.find(u=> u.id === whoHasCursor);
  return (
    <div className="App">
      <ToastContainer/>
      <div className="vWrapper">
        {loadStatus !== LoadStatus.STREAMING && <div className='vidLoading'>
          {loadStatus === LoadStatus.IDLE && <span className='loadText'>Please wait...</span>}
          {loadStatus === LoadStatus.REQUESTING_ROOM && <span className='loadText'>Please wait, your room is being set up...</span>}
          {loadStatus === LoadStatus.WAITING_FOR_MEDIA && <span className='loadText'>Almost there...</span>}
          {loadStatus === LoadStatus.ERROR && <span className='loadText'>An error occurred. Please restart the activity.</span>}
          {loadStatus === LoadStatus.ERROR_SPROOM && <span className='loadText'>An error occurred creating the room.<br/>We currently may be at capacity, so please try again later.</span>}
          {loadStatus !== LoadStatus.ERROR && loadStatus !== LoadStatus.ERROR_SPROOM && <LoadingSpinner/>}
        </div>}
        <video className="video" ref={containerRef} onMouseMove={onMouseMove} onMouseDown={onMouseUpDown} onMouseUp={onMouseUpDown} onWheel={onScroll} playsInline controls={false}></video>
      </div>
      <div className='sideMenu'>
        <div className="ibutton">
          <i className="fa fa-cog" aria-hidden="true"/>
          <div className='menu prevent-select'>
            <ul>
              <li>Restart Room</li>
              <li onClick={()=>{setShowDebug(!showDebug)}}>Toggle Debug</li>
            </ul>
          </div>
        </div>
        <div className="ibutton" style={whoHasCursor?{}:{display:"none"}}>
          <UserPfp user={whoHasCursorUser} size={64}/>
          <div className='cIcon'>
            <i className="fa fa-mouse-pointer" aria-hidden="true"/>
          </div>
          <div className='menu prevent-select'>
            <ul>
              <li className='unselectable'><em>{users.find(u=>u.id===whoHasCursor)?.username} has the cursor</em></li>
              {whoHasCursor !== undefined && whoHasCursor === currentUserId && users.filter(u=> u.id !== currentUserId).map((user) => (
              <li>
                <div className='pfpselect' onClick={()=>{
                    passCursorToUser(user.id);
                  }}>
                  <UserPfp user={user} size={32}/>
                  <span>Pass the cursor to <b>{user.username}</b></span>
                </div>
              </li>
              ))}
            </ul>
          </div>
        </div>
        {/*<div className="ibutton">
          <button onClick={()=>{
            start("ws://localhost:8080");
          }}>test</button>
        </div>*/}
      </div>
      {showDebug && <div className='debugInfo'>
        <h3>Debug!</h3>
        <p>RoomID: {discordSdk?.instanceId}</p>
        <p>WhoHasCursor: {whoHasCursor}</p>
        <p>CurrentUserHasCursor: {currentUserHasCursor() ? "yes":"no"}</p>
        <p>CurrentUser: {currentUserId}</p>
        <p>DecodedFrames: {playerStats?.decodedFrames}</p>
        <p>DroppedFrames: {playerStats?.droppedFrames}</p>
        <p>totalSegmentCount: {playerStats?.totalSegmentCount}</p>
        <p>users: {users.length}</p>
      </div>}
    </div>
  );
}
